import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

import { FULL_DECK } from "@/data/tarotDeck";
import { TarotCard } from "@/components/TarotCard";
import { useLanguage } from "@/context/LanguageContext";
import { playFlip, playChime, primeAudio } from "@/lib/sfx";
import { dailyCacheKey, todayKey } from "@/lib/dailyCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Deterministic hash seeded by first name + UTC day.
const hashSeed = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// Same first name + same UTC day = same card.
const pickTodayCard = (firstName) => {
  const key = todayKey();
  const seed = `${firstName.trim().toLowerCase()}|${key}`;
  const idx = hashSeed(seed) % FULL_DECK.length;
  return { card: FULL_DECK[idx], dateKey: key };
};

export const CardOfTheDay = ({ firstName, actions = null }) => {
  const { t, language } = useLanguage();
  const { card, dateKey } = useMemo(() => pickTodayCard(firstName), [firstName]);
  const [revealed, setRevealed] = useState(false);
  const [text, setText] = useState("");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto-reveal if we already fetched the interpretation for today's card in this language.
  useEffect(() => {
    const cached = window.localStorage.getItem(dailyCacheKey(firstName, dateKey, language));
    if (cached) {
      let parsed;
      try {
        parsed = JSON.parse(cached);
      } catch (_) {
        parsed = { text: cached, question: "" };
      }
      setText(parsed.text || "");
      setQuestion(parsed.question || "");
      setRevealed(true);
    } else {
      // Reset when the language changes and we have no cache for it yet.
      setText("");
      setQuestion("");
      setRevealed(false);
    }
  }, [firstName, dateKey, language]);

  const handleReveal = async () => {
    if (revealed || loading) return;
    setLoading(true);
    // Flip the card immediately so it feels responsive — with the same flip + chime as the Reading page.
    primeAudio();
    playFlip();
    setTimeout(playChime, 350);
    setRevealed(true);
    try {
      const res = await fetch(`${API}/daily_card`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language, card_key: card.key, first_name: firstName }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const value = (data && data.text) || "";
      const q = (data && data.question) || "";
      setText(value);
      setQuestion(q);
      if (value) {
        try {
          window.localStorage.setItem(dailyCacheKey(firstName, dateKey, language), JSON.stringify({ text: value, question: q }));
        } catch (_) {
          /* localStorage may be full — ignore */
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(t.reading.failed);
      // Flip the card back down on failure so the user can retry.
      setRevealed(false);
    } finally {
      setLoading(false);
    }
  };

  const humanDate = new Date(dateKey + "T12:00:00Z").toLocaleDateString(
    language === "fr" ? "fr-FR" : "en-US",
    { day: "numeric", month: "long", year: "numeric" }
  );

  return (
    <section
      data-testid="card-of-the-day-section"
      className="mb-10 rounded-md border border-gold/30 bg-oxblood-surface/60 p-6 md:p-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
        {/* Card visual */}
        <div className="md:col-span-4 flex flex-col items-center gap-4">
          <TarotCard
            card={card}
            revealed={revealed}
            size="lg"
            glow={revealed}
            onClick={!revealed && !loading ? handleReveal : undefined}
            selectable={!revealed && !loading}
            testId="card-of-the-day-card"
          />
        </div>

        {/* Copy + CTA */}
        <div className="md:col-span-8">
          <div className="text-sm md:text-base tracking-[0.4em] uppercase text-[#14103A] font-bold mb-3">
            ✧ {t.home.cardOfTheDay}
          </div>
          <p
            className="text-[0.68rem] md:text-[0.72rem] tracking-[0.32em] uppercase text-gold/80 mb-4"
            data-testid="card-of-the-day-date"
          >
            {t.home.cardOfTheDayDate} · {humanDate}
          </p>

          <AnimatePresence mode="wait">
            {!revealed ? (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <p
                  data-testid="card-of-the-day-intro"
                  className="text-ivory font-serif italic font-bold leading-relaxed text-base md:text-lg"
                >
                  <span className="shimmer-gold not-italic">{firstName}</span>, {t.home.cardOfTheDayIntro}
                </p>
                <button
                  type="button"
                  onClick={handleReveal}
                  disabled={loading}
                  data-testid="card-of-the-day-reveal"
                  className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.72rem] tracking-[0.35em] uppercase font-semibold disabled:opacity-40"
                >
                  {loading ? t.home.cardOfTheDayRevealing : t.home.cardOfTheDayReveal} ✦
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="reflection"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.35 }}
              >
                {text ? (
                  <>
                    <p
                      data-testid="card-of-the-day-text"
                      className="font-serif italic text-ivory/90 leading-relaxed text-base md:text-lg whitespace-pre-wrap"
                    >
                      {text}
                    </p>
                    {question && (
                      <div
                        data-testid="card-of-the-day-question"
                        className="mt-6 rounded-md border border-gold/50 bg-gold/10 px-5 py-4"
                      >
                        <div className="text-[0.68rem] tracking-[0.35em] uppercase text-gold font-bold mb-2">
                          ✦ {t.home.questionOfTheDay}
                        </div>
                        <p className="font-serif text-ivory text-lg md:text-xl leading-snug">{question}</p>
                      </div>
                    )}
                    {actions}
                  </>
                ) : (
                  <div
                    data-testid="card-of-the-day-thinking"
                    className="flex items-center gap-4 text-ivory font-serif italic font-bold text-2xl md:text-3xl leading-snug"
                  >
                    <span className="pulse-square" />
                    <span>{t.reading.thinking}</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default CardOfTheDay;
